import { computeRotationAngleWasm, initWasmModule } from '@/wasm/wasm'
import * as Sentry from '@sentry/react'
import type { BoundaryComputeMode } from './types/boundary-compute-mode'
import type { BoundaryComputeOptions } from './types/boundary-compute-options'
import type { ResizeComputeOptions } from './types/resize-compute-options'
import type { ResizeParams } from './types/resize-params'
import type { ResizeResult } from './types/resize-result'
import type { RotationAngleParams } from './types/rotation-angle-params'
import type { RotationAngleResult } from './types/rotation-angle-result'
import {
  computeResizeConstrainedAspectJS,
  computeResizeJS,
  computeRotationAngleJS,
  isCornerHandle,
} from './helpers/bounding-box-js'

const DEFAULT_HOT_MS = 80

function nowMs(): number {
  // performance.now() is monotonic; Date.now() is a safe fallback.
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
}

function createAdaptiveGate(defaultHotMs: number = DEFAULT_HOT_MS) {
  let lastCallAt = -Infinity

  return (opts?: BoundaryComputeOptions) => {
    const mode: BoundaryComputeMode = opts?.mode ?? 'adaptive'

    if (mode === 'js') return { mode, useWasm: false }
    if (mode === 'wasm') return { mode, useWasm: true }

    const hotMs = opts?.hotMs ?? defaultHotMs
    const t = nowMs()
    const isHot = t - lastCallAt < hotMs

    lastCallAt = t

    // Hot burst = prefer JS (stable per-frame); idle = try WASM.
    return { mode, useWasm: !isHot }
  }
}

const gateRotate = createAdaptiveGate()

/**
 * Synchronous hot-path helpers (JS-only).
 * Use these inside pointermove / animation loops to avoid async jitter.
 */
export function computeResizeSync(
  params: ResizeParams,
  opts?: Pick<ResizeComputeOptions, 'constrainAspect' | 'axis'>
): ResizeResult {
  if (opts?.constrainAspect && isCornerHandle(params.direction)) {
    return computeResizeConstrainedAspectJS(params, { axis: opts.axis })
  }

  if (opts?.constrainAspect && !isCornerHandle(params.direction)) {
    const axis =
      opts.axis ?? (params.direction === 'n' || params.direction === 's' ? 'height' : 'width')

    return computeResizeConstrainedAspectJS(params, { axis })
  }

  return computeResizeJS(params)
}

export function computeRotationAngleSync(params: RotationAngleParams): RotationAngleResult {
  return computeRotationAngleJS(params)
}

/**
 * Initialize WASM early in app lifecycle
 * Call this once when the app starts
 */
export async function initBoundingBoxWasm(): Promise<void> {
  try {
    await initWasmModule()
  } catch (error) {
    console.warn('Failed to initialize WASM, will fallback to JS:', error)
  }
}

/**
 * Boundary function choosing between optimized JS and WASM.
 * Defaults to 'adaptive': JS during hot call bursts, WASM when infrequent.
 */
export async function computeRotationAngle(
  params: RotationAngleParams,
  opts?: BoundaryComputeOptions
): Promise<RotationAngleResult> {
  const attributes = { mode: opts?.mode ?? 'adaptive' }
  const run = async () => {
    const decision = gateRotate(opts)

    try {
      if ((opts?.mode ?? 'adaptive') === 'js') {
        return computeRotationAngleJS(params)
      }

      if (decision.useWasm) {
        const result = await computeRotationAngleWasm(params)

        Sentry.setContext('wasm', { success: true, implementation: 'go-wasm-bounding-box' })
        return result
      }

      return computeRotationAngleJS(params)
    } catch (error) {
      if ((opts?.mode ?? 'adaptive') !== 'js') {
        console.warn('WASM failed, falling back to JS:', error)
        Sentry.captureException(error)
        Sentry.setContext('wasm', {
          success: false,
          error: (error as Error).message,
          fallback: 'js-bounding-box',
        })
      }

      return computeRotationAngleJS(params)
    }
  }

  if (opts?.noTelemetry) {
    return await run()
  }

  return await Sentry.startSpan(
    { name: 'boundingBox.rotation.compute', op: 'boundingBox.compute', attributes },
    run
  )
}
